/**
 * Checks whether `currentDate` is before (or equal to) `endDateISO`
 * and optionally after (or equal to) `startDateISO`, in a given time zone.
 *
 * Dates are converted to the specified time zone before comparison.
 * Defaults to Central Time ('America/Chicago').
 * Returns false if endDate is missing or any relevant date is invalid.
 */
export function isDateInSpecifiedRange(
  currentDate: Date,
  startDateISO?: string | null,
  endDateISO?: string | null,
  timeZone: string = 'America/Chicago'
): boolean {
  // Converts an ISO date string to a Date object adjusted to the given time zone.
  const toZonedDate = (isoString?: string | null): Date | null => {
    if (!isoString) return null;

    const parsed = new Date(isoString);
    if (isNaN(parsed.getTime())) return null;

    // Format the date using the desired time zone to extract parts
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(parsed);

    // Extract date and time components from the formatted parts
    const getPart = (type: string) =>
      parts.find((p) => p.type === type)?.value || '00';

    // Reconstruct a valid date string with time zone-adjusted values
    const dateStr = `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;

    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const zonedStart = toZonedDate(startDateISO);
  const zonedEnd = toZonedDate(endDateISO);
  const zonedNow = toZonedDate(currentDate.toISOString());

  // End date is required
  if (!zonedEnd || !zonedNow) return false;

  const isAfterStart = !zonedStart || zonedNow >= zonedStart;
  const isBeforeEnd = zonedNow <= zonedEnd;

  return isAfterStart && isBeforeEnd;
}
